import AngleIcon from "@svg/angle-icon";
import LockIcon from "@svg/lock-icon";
import PlayIcon from "@svg/title-play-icon";
import {
  COURSE_TYPE,
  CourseContent,
  CoursePreview,
  CoursePreviewSteps,
  LESSON_STEP_TYPE,
  StepPreviewItem,
} from "@types";
import { toFarsiNumber } from "@utils/digits";
import { formatTime } from "@utils/player";
import classNames from "classnames";
import { forwardRef, useEffect, useMemo, useState } from "react";
import {
  Button,
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from "@shared/components/button";

import HeadlineItem from "./headline-item";
import { IconButton } from "@shared/components/icon-button";
import { ICON_SIZE } from "@shared/components/icon-button";
import { useLayoutContext } from "@context/layout";
import RelatedFiles from "./RelatedFils";
import { headlineSupplementaryContent } from "@modules/course/access/side-bar/lesson-details/Icon";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import Accordion from "legacy/elements/accordion";

interface Props {
  id?: string;
  lessonsCount: number;
  preview: CoursePreview | undefined;
  courseTitle: string;
  isPack?: boolean;
  className?: string;
  courseContent?: CourseContent;
}

const validHeadlineItem = [
  COURSE_TYPE.COURSE,
  COURSE_TYPE.EXAM,
  COURSE_TYPE.PRACTICE,
  COURSE_TYPE.NOTE,
  LESSON_STEP_TYPE.GLOSSARY,
  LESSON_STEP_TYPE.FAQ,
  LESSON_STEP_TYPE.RELATED_JOB,
  LESSON_STEP_TYPE.INTERVIEW_QUESTIONS,
  LESSON_STEP_TYPE.SUMMARY_NOTE,
];

const collectionDataFormatter = (preview: CoursePreview | undefined) => {
  const adaptedCollectionData = (preview?.collections || []).reduce(
    (result, item) => {
      const steps = item.steps.filter(
        (step) =>
          (step.status_price === "none-free" &&
            step.online_title !== "چکیده") ||
          (step.status_price !== "abstract" && step.online_title !== "چکیده")
      );
      if (steps.length) {
        result.push({ ...item, steps });
      }
      return result;
    },
    [] as CoursePreview["collections"]
  );
  return adaptedCollectionData;
};

const Headline = forwardRef<HTMLElement, Props>(
  (
    {
      id,
      lessonsCount,
      preview,
      courseTitle,
      isPack = false,
      className,
      courseContent,
    },
    ref
  ) => {
    const layoutData = useLayoutContext();

    const isLogin = layoutData?.userData?.is_login;
    const [accordionsStatus, setAccordionsStatus] = useState<boolean[]>([]);
    const [collections, setCollections] = useState<
      CoursePreview["collections"] | undefined
    >(collectionDataFormatter(preview));

    const handleCloseAllAccordions = () => {
      setAccordionsStatus(collections?.map(() => false) || []);
    };

    const isThereAnyOpenAccordion = useMemo(() => {
      return accordionsStatus.findIndex((status) => status) !== -1;
    }, [accordionsStatus]);

    const showCourseContent =
      courseContent && (courseContent.has_file || courseContent.has_pdf);
    const courseContentItems = [];
    if (courseContent?.has_pdf) {
      courseContentItems.push("اسلایدها و یادداشت‌های مدرس");
    }
    if (courseContent?.has_file) {
      courseContentItems.push("کدها و پروژه‌ها");
    }

    useEffect(() => {
      const adaptedCollectionData = preview?.collections.reduce(
        (result, item) => {
          const steps = item.steps.filter((step) => {
            const cond1 =
              step.status_price === "none-free" &&
              step.online_title !== "چکیده";
            const cond2 =
              step.status_price !== "abstract" && step.online_title !== "چکیده";
            const textractorTypes = [
              LESSON_STEP_TYPE.GLOSSARY,
              LESSON_STEP_TYPE.FAQ,
              LESSON_STEP_TYPE.RELATED_JOB,
              LESSON_STEP_TYPE.INTERVIEW_QUESTIONS,
            ];
            const cond3 = textractorTypes.includes(step.type as any);
            return cond1 || cond2 || cond3;
          });
          if (steps.length) {
            result.push({ ...item, steps });
          }
          return result;
        },
        [] as CoursePreview["collections"]
      );

      setCollections(adaptedCollectionData);
      setAccordionsStatus(
        adaptedCollectionData?.map(({ expanded }) =>
          isPack ? false : expanded
        ) || []
      );
    }, [preview]);

    const hasTitlePrefix = !useIsMobile(576);
    let stepIndexAccordion = 0;

    let stepIndex = 0;
    const allSteps = collections?.reduce(
      (collectionPrev, collectionCurrent) => {
        if (collectionCurrent.steps.length) {
          const stepItem = (
            collectionCurrent.steps as CoursePreviewSteps[]
          ).reduce((stepPrev, stepCurrent) => {
            const { hours, minutes, seconds } = formatTime(
              Number(stepCurrent.duration)
            );
            const preparedTime =
              hours != "00"
                ? `${hours}:${minutes}:${seconds}`
                : `${minutes}:${seconds}`;
            if (
              stepCurrent.open_for_guest ||
              (stepCurrent.is_free && isLogin)
            ) {
              stepPrev[stepIndex] = {
                id: stepCurrent.id || null,
                collectionTitle: collectionCurrent.name,
                title: stepCurrent.online_title,
                subtitleSrc: stepCurrent.subtitle,
                videoSources: [
                  {
                    label: "۷۲۰p",
                    url: String(stepCurrent.online_link),
                  },
                  {
                    label: "۴۸۰p",
                    url: String(stepCurrent.online_link_480),
                  },
                ],
                time: preparedTime,
              };
              stepIndex++;
            }
            return stepPrev;
          }, {} as StepPreviewItem);
          collectionPrev = { ...collectionPrev, ...stepItem };
        }
        return collectionPrev;
      },
      {} as StepPreviewItem
    );

    const prepareProps = (step: CoursePreviewSteps) => {
      const { hours, minutes, seconds } = formatTime(Number(step.duration));

      const isFree = step.is_free;
      const openForGuest = step.open_for_guest;

      const supplementaryContents = headlineSupplementaryContent();
      const supplementaryTitle =
        supplementaryContents[step?.type as keyof typeof supplementaryContents]
          ?.label;

      const itemProps = {
        isFree,

        duration:
          hours != "00"
            ? `${hours}:${minutes}:${seconds}`
            : `${minutes}:${seconds}`,
        iconComponent: isFree ? PlayIcon : LockIcon,
        openForGuest,
        subtitle: courseTitle,
        title: supplementaryTitle || step.online_title || step.name || "",
        ...(!isLogin &&
          isFree &&
          !openForGuest && {
            message: (
              <span className="text-fds-tertiary body-md">
                نیازمند
                <Button
                  variant={BUTTON_VARIANT.TEXT_ACTION}
                  color={BUTTON_COLOR.PRIMARY}
                  size={BUTTON_SIZE.SMALL}
                  href="/my-account"
                >
                  ورود/ثبت‌نام
                </Button>
              </span>
            ),
          }),
        stepIndex: stepIndexAccordion,
        allSteps,
        courseType: step.type || LESSON_STEP_TYPE.GENERAL,
      };
      if (openForGuest || (isFree && isLogin)) {
        stepIndexAccordion++;
      }
      return itemProps;
    };

    const contentText = useMemo(() => {
      if (collections?.length && lessonsCount)
        return `شامل ${toFarsiNumber(collections?.length)} فصل و
              ${toFarsiNumber(lessonsCount)} درس`;
      else if (collections?.length)
        return `شامل ${toFarsiNumber(collections?.length)} فصل`;
      else if (lessonsCount) `شامل ${toFarsiNumber(lessonsCount)} درس`;
      else return "";
    }, [collections, lessonsCount]);

    return (
      <section ref={ref} className={classNames(className)} id={id}>
        <div>
          <h2 className="headline-sm md:headline-md">سرفصل‌ها</h2>
          <div className="flex items-center mb-3  justify-between">
            {contentText && (
              <p className="body-sm sm:body-md text-fds-secondary">
                {contentText}
              </p>
            )}
            <Button
              onClick={handleCloseAllAccordions}
              variant={BUTTON_VARIANT.TEXT}
              color={BUTTON_COLOR.PRIMARY}
              size={BUTTON_SIZE.MEDIUM}
              className={classNames(
                "text-fds-brand",
                isThereAnyOpenAccordion ? "visible" : "invisible"
              )}
            >
              بستن همه بخش‌ها
            </Button>
          </div>
          <div className="border border-fds-default rounded-12">
            {collections?.map((collection, index) => {
              return (
                <Accordion
                  key={index}
                  className={(isOpen) =>
                    classNames(
                      "h-auto py-2.5   md:py-3  pl-2  pr-5    bg-fds-secondary border-t border-fds-default",
                      index === 0 && "rounded-t-12 border-t-0",
                      {
                        "rounded-b-12":
                          index === collections?.length - 1 &&
                          !showCourseContent,
                      },
                      { "rounded-b-none border-b": isOpen }
                    )
                  }
                  title={`${hasTitlePrefix ? "فصل" : ""} ${toFarsiNumber(
                    index + 1
                  )}. ${toFarsiNumber(collection.name)}`}
                  titleClassName={classNames(
                    "title-md-demibold text-fds-primary text-ellipsis whitespace-nowrap overflow-hidden"
                  )}
                  contentClassName=""
                  openAccordion={(isOpen) =>
                    setAccordionsStatus((prev) =>
                      prev.map((accordionStatus, i) =>
                        i === index ? isOpen : accordionStatus
                      )
                    )
                  }
                  iconComponent={(isOpen) => (
                    <IconButton
                      size={ICON_SIZE.MEDIUM}
                      iconClassName={classNames(
                        "text-fds-icon-secondary basis-4 mdx:basis-4 grow-0 shrink-0  transition-all duration-300 ease-linear w-4     h-4    ",
                        isOpen ? "rotate-180" : "rotate-0"
                      )}
                      icon={<AngleIcon />}
                      aria-label="باز و بستن محتوا"
                    />
                  )}
                  open={accordionsStatus[index]}
                >
                  <div className="sm:py-2.5 py-4 relative ">
                    {collection.steps
                      .filter(
                        (stepItem) =>
                          validHeadlineItem.includes(stepItem.type as any) ||
                          !stepItem.type
                      )
                      .map((step, i) => {
                        const itemProps = prepareProps(step);
                        return (
                          <HeadlineItem key={i} {...itemProps} id={step.id} />
                        );
                      })}
                  </div>
                </Accordion>
              );
            })}
            {showCourseContent ? (
              <RelatedFiles
                {...{
                  accordionsStatus,
                  allSteps,
                  stateIndex: (collections?.length ?? 0) + 1,
                  courseContentItems,
                  courseTitle,
                  setAccordionsStatus,
                  stepIndexAccordion,
                }}
              />
            ) : null}
          </div>
        </div>
      </section>
    );
  }
);

Headline.displayName = "Headline";
export default Headline;
